/**
 * Core type definitions for the Display Abstraction Layer.
 *
 * This layer decouples domain data (Volume, Centerline) from rendering strategy
 * (how they appear in different viewport types).
 *
 * Key concepts:
 * - DisplayFeature: Describes WHAT to display (data + properties)
 * - DisplayObject: Knows HOW to render a feature in a specific view mode
 * - DisplayFeatureManager: Central registry of features
 * - DisplayObjectRegistry: Factory registry for DisplayObject creation
 */

import type vtkImageData from '@kitware/vtk.js/Common/DataModel/ImageData';
import vtkActor from '@kitware/vtk.js/Rendering/Core/Actor';

import { Vec3, Vec2 } from '../domain';
import type { Centerline, Keypoint, RGB } from '../domain';
import type { CenterlineNode } from '../domain/centerline';
import type { DiameterEdit } from '../domain/sizing/diameter-edit';
import type { KeypointPlacement } from '../domain/sizing/keypoint-placement';
import type { MeasurementResult } from '../domain/sizing/measurement-result';
import { InteractionEvent } from '../interaction';
import type { CurrentLocation } from '../services';
import { IViewport } from '../viewports';
/**
 * Feature types determine rendering strategy.
 * Each type has specific DisplayObject implementations for different view modes.
 */
export enum DisplayFeatureType {
    CENTERLINE = 'centerline',
    KEYPOINT = 'keypoint',
    STRUCTURE_SET = 'structure-set',
    MEASUREMENT = 'measurement',
    CURRENT_LOCATION = 'current-location',
    SIZING = 'sizing',
    TEP_OVERLAY = 'tep-overlay',
    //Dev
    SPHERE = 'sphere',
}

/**
 * View modes - how features are rendered in different viewport types.
 * Aligned with ViewportType enum for consistent mapping.
 */
export enum ViewMode {
    VIEW_3D = '3d',
    VIEW_2D_AXIAL = '2d_axial',
    VIEW_2D_SAGITTAL = '2d_sagittal',
    VIEW_2D_CORONAL = '2d_coronal',
    VIEW_2D_OBLIQUE = '2d_oblique',
    VIEW_CPR_STRETCHED = 'cpr_stretched',
    VIEW_CPR_STRAIGHTENED = 'cpr_straightened',
}

export interface CenterlineDisplayProperties {
    /** RGB color values, each in range 0-1 */
    color?: RGB;
    /** Opacity value in range 0-1 */
    opacity?: number;
    /** Tube diameter in mm for tube-based features (centerlines) */
    tubeDiameter?: number;
    /** Tube diameter in mm for the active element */
    activeTubeDiameter?: number;
    /** ID of the currently active centerline (set by workflow, read by display object) */
    activeCenterlineId?: string;
}

export interface KeypointDisplayProperties {
    /** RGB color values, each in range 0-1 */
    color?: RGB;
    /** Opacity value in range 0-1 */
    opacity?: number;
    /** Point size for point-based features */
    pointSize?: number;
}

export interface StructureSetDisplayProperties {
    /** Per-label color: labelValue → RGB */
    colors: ReadonlyMap<number, RGB>;
    /** Structure kind, used to build structure keys */
    kind: 'segment' | 'tumor';
    /** Currently hovered structure key (e.g. "segment:3"), or null */
    hoveredKey: string | null;
    /** Currently selected structure keys */
    selectedKeys: ReadonlySet<string>;
}

export interface MeasurementDisplayProperties {
    /** RGB color values, each in range 0-1 */
    color?: RGB;
    /** Opacity value in range 0-1 */
    opacity?: number;
    /** Line width for line-based features */
    lineWidth?: number;
    /** Point size for point-based features */
    pointSize?: number;
}

export interface CurrentLocationDisplayProperties {
    /** Opacity value in range 0-1 (applied to 3D sphere) */
    opacity?: number;
    sphere: {
        radius: number;
        color: RGB;
    };
    crosshair: {
        color: string;
    };
}

export interface SizingDisplayProperties {
    /** Color for keypoint markers */
    keypointColor?: string;
    /** Color for diameter lines */
    diameterColor?: string;
    /** Color for length labels */
    lengthColor?: string;
    /** Font size for measurement labels */
    fontSize?: number;
}

export interface SizingDraftDisplay {
    readonly kind: 'length' | 'angle';
    /** Placed points resolved to world positions, in slot order (1–3 points). */
    readonly points: readonly Vec3[];
    /**
     * Live preview: the current cursor/centerline position filling the next
     * unplaced slot. Null when the draft is complete or there is no location.
     */
    readonly previewPoint?: Vec3 | null;
    /**
     * Length preview only: centerline path points from the placed start to the
     * preview point (≥2 points). Null when the two don't share a vessel path
     * (not computable) — renderers draw no provisional tube in that case.
     */
    readonly previewPath?: readonly Vec3[] | null;
    /**
     * Length preview only: [startNodeId, previewNodeId] for the provisional CPR
     * lane. Null when not previewable. Mirrors `previewPath` computability.
     */
    readonly previewLengthNodeIds?: readonly [number, number] | null;
    /**
     * Angle preview only: the wedge resolved by slot (P1/Vertex/P2), with the
     * cursor substituted into whichever slot is still unplaced. `arm2` is null
     * while only the vertex and one arm are known (single-arm line). Null when
     * the vertex is not yet known (nothing meaningful to draw).
     */
    readonly previewAngle?: {
        readonly vertex: Vec3;
        readonly arm1: Vec3;
        readonly arm2: Vec3 | null;
    } | null;
}

export interface SizingDisplayData {
    readonly placements: ReadonlyMap<string, KeypointPlacement>;
    readonly ghostPlacements: ReadonlyMap<string, readonly KeypointPlacement[]>;
    readonly measurements: ReadonlyMap<string, MeasurementResult>;
    readonly nodePool: ReadonlyMap<number, CenterlineNode>;
    readonly diameterEdits: ReadonlyMap<number, DiameterEdit>;
    readonly centerlines: ReadonlyMap<string, Centerline>;
    readonly selectedMeasurementId: string | null;
    readonly currentNodeId: number | null;
    readonly activeCenterlineId: string | null;
    /** In-progress custom-measurement draft markers (markers only, no trace). */
    readonly draft: SizingDraftDisplay | null;
}

export interface TepOverlayDisplayProperties {
    /** Overlay opacity for non-zero voxels (0-1) */
    opacity: number;
}

export interface TepOverlayDisplayData {
    /** The TEP vtkImageData volume to render */
    readonly tepVolume: vtkImageData | null;
}

export interface SphereDisplayProperties {
    /** RGB color values, each in range 0-1 */
    color?: RGB;
    /** Opacity value in range 0-1 */
    opacity?: number;
    /** Sphere radius in mm */
    radius?: number;
}

/** Type-level map: DisplayFeatureType → its properties type */
export interface DisplayPropertiesMap {
    [DisplayFeatureType.CENTERLINE]: CenterlineDisplayProperties;
    [DisplayFeatureType.KEYPOINT]: KeypointDisplayProperties;
    [DisplayFeatureType.STRUCTURE_SET]: StructureSetDisplayProperties;
    [DisplayFeatureType.MEASUREMENT]: MeasurementDisplayProperties;
    [DisplayFeatureType.CURRENT_LOCATION]: CurrentLocationDisplayProperties;
    [DisplayFeatureType.SIZING]: SizingDisplayProperties;
    [DisplayFeatureType.TEP_OVERLAY]: TepOverlayDisplayProperties;
    [DisplayFeatureType.SPHERE]: SphereDisplayProperties;
}

/** Type-level map: DisplayFeatureType → its data type */
export interface DisplayDataMap {
    [DisplayFeatureType.CENTERLINE]: Map<string, Centerline>;
    [DisplayFeatureType.KEYPOINT]: Map<string, Keypoint>;
    [DisplayFeatureType.STRUCTURE_SET]: unknown;
    [DisplayFeatureType.MEASUREMENT]: unknown;
    [DisplayFeatureType.CURRENT_LOCATION]: CurrentLocation | null;
    [DisplayFeatureType.SIZING]: SizingDisplayData;
    [DisplayFeatureType.TEP_OVERLAY]: TepOverlayDisplayData;
    [DisplayFeatureType.SPHERE]: unknown;
}

/** Resolve data type for a feature type */
export type DisplayDataFor<T extends DisplayFeatureType> = DisplayDataMap[T];

/** Union of all possible properties */
export type AnyDisplayProperties = DisplayPropertiesMap[DisplayFeatureType];

/**
 * DisplayFeature describes WHAT to display, not HOW.
 *
 * Features have identity-immutable fields (id, type) with mutable data and visualization properties.
 * They are managed by DisplayFeatureManager and rendered by DisplayObjects.
 *
 * @template TData - The domain data type (Volume, Centerline, etc.)
 */
export interface IDisplayFeature<
    T extends DisplayFeatureType = DisplayFeatureType,
    TData = DisplayDataFor<T>,
> {
    /** Unique identifier (e.g., 'evar-ct-volume', 'evar-centerline-main') */
    readonly id: string;

    /** Feature type determines which DisplayObject can render it */
    readonly type: T;

    /** Reference to domain data (Volume, Centerline, etc.) */
    data: TData;

    /** Visualization properties (mutable) */
    properties: DisplayPropertiesMap[T];

    /** Per-view-mode visibility (true = visible in that mode) */
    viewModeVisibility: Map<ViewMode, boolean>;
}

/**
 * DisplayObject knows HOW to render a feature in a specific view mode.
 *
 * Each (FeatureType, ViewMode) combination has a specific DisplayObject implementation.
 * DisplayObjects manage VTK actors and their lifecycle.
 */
export interface IDisplayObject {
    /** The feature this object renders */
    readonly feature: IDisplayFeature;

    /** The view mode this object is configured for */
    readonly viewMode: ViewMode;

    /**
     * Create VTK actors and add to renderer.
     * Called once when feature is added to viewport.
     */
    initialize(viewport: IViewport): void;

    /**
     * Update rendering when feature properties change.
     * Called when FEATURE_UPDATED event is received.
     */
    update(): void;

    /**
     * Remove from renderer and clean up VTK resources.
     * Called when feature is removed or viewport is disposed.
     */
    dispose(): void;

    getActors(): vtkActor[];
}

/**
 * Factory function for creating DisplayObjects.
 * Registered with DisplayObjectRegistry for each (FeatureType, ViewMode) combination.
 */
export type DisplayObjectFactory = (feature: IDisplayFeature, viewMode: ViewMode) => IDisplayObject;

// ============================================================================
// Event Payloads
// ============================================================================

/**
 * Payload for FEATURE_ADDED event.
 */
export interface FeatureAddedPayload {
    feature: IDisplayFeature;
}

/**
 * Payload for FEATURE_REMOVED event.
 */
export interface FeatureRemovedPayload {
    id: string;
    feature: IDisplayFeature;
}

/**
 * Payload for FEATURE_UPDATED event.
 */
export interface FeatureUpdatedPayload {
    id: string;
}

/**
 * Payload for FEATURE_VISIBILITY_CHANGED event.
 */
export interface FeatureVisibilityChangedPayload {
    id: string;
}

export interface IPickable {
    /** Whether this object is currently pickable */
    readonly pickable: boolean;

    /** Get VTK actors that should be registered for picking */
    getPickableActors(): vtkActor[];

    /**
     * Handle a pick on this object.
     * Return PickResult with domain-specific data, or null if pick should be ignored.
     */
    onPick(pickInfo: PickInfo): PickResult | null;
}

export interface IHoverable {
    /** Whether this object responds to hover */
    readonly hoverable: boolean;

    /**
     * Called when pointer hovers over this object.
     * Return true if hover was handled (e.g., highlight applied).
     */
    onHoverStart(pickInfo: PickInfo): boolean;

    /** Called when pointer leaves this object */
    onHoverEnd(): void;
}

export interface IDraggable {
    /** Get drag handles (if any) */
    getDragHandles(): DragHandle[];

    /** Called when drag starts. Return false to cancel drag. */
    onDragStart(handle: DragHandle | null, event: InteractionEvent): boolean;

    /** Called during drag */
    onDrag(event: InteractionEvent): void;

    /** Called when drag ends */
    onDragEnd(event: InteractionEvent): void;

    /** Called if drag is cancelled (e.g., Escape key) */
    onDragCancel(): void;
}

export interface PickInfo {
    /** The picked actor (can be null if no actor) */
    actor: vtkActor | null;
    /**  */
    overlayHit: boolean;
    /**  */
    screenPosition: Vec2;
    /** World coordinates of pick point */
    worldPosition: Vec3;
    /** Cell ID if cell picking enabled */
    cellId: number;
    /** Point ID if point picking enabled */
    pointId: number;
    /** The interaction event that triggered the pick */
    event: InteractionEvent;
}

export interface PickResult {
    /** Type discriminator for the pick result */
    type: string;
    /** The display object that was picked */
    displayObject: IDisplayObject;
    /** The picked actor (can be null if no actor) */
    actor: vtkActor | null;
    /** World position of the picked element */
    worldPosition: Vec3;
    pickInfo: PickInfo;
}

export interface DragHandle {
    id: string;
    position: { x: number; y: number }; // Canvas coordinates
    cursor: string; // CSS cursor style
    hitRadius: number; // Hit test radius in pixels
}

export interface IOverlayObject {
    /** Unique identifier */
    readonly id: string;

    /** Whether this object handles events */
    readonly interactive: boolean;

    /** Whether this object is currently visible */
    visible: boolean;

    /** Z-order for rendering and hit testing */
    readonly zIndex: number;

    /** Whether to clip rendering based on camera clipping range (default: false) */
    readonly clipByCamera: boolean;

    worldPosition?: [number, number, number] | null;

    canvasPosition?: { x: number; y: number } | null;

    /** Hit test for interaction */
    hitTest(canvasX: number, canvasY: number): boolean;

    /** Render to 2D context */
    render(ctx: CanvasRenderingContext2D, viewport: IViewport): void;

    /** Called when viewport camera changes (for world→screen projection) */
    onCameraChanged?(viewport: IViewport): void;
}
